import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Calendar, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerProfile = () => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });

  const partnerToken = localStorage.getItem('partnerToken');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${partnerToken}` }
  };

  // Fetch profile
  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/partner/profile`, axiosConfig);
      setProfile(response.data);
      setFormData({
        name: response.data.name,
        email: response.data.email,
        mobile: response.data.mobile,
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!formData.mobile || !/^\d{10}$/.test(formData.mobile.trim().replace(/[\s\-\+]/g, '').slice(-10))) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    setIsLoading(true);

    try {
      await axios.put(`${API}/partner/profile`, formData, axiosConfig);
      toast.success('Profile updated successfully!');
      localStorage.setItem('partnerName', formData.name);
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(e => e.msg?.replace('Value error, ', '')).join(', ') : detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="partner-profile">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Profile</h1>
          <p className="text-slate-400 mt-1">Manage your account information</p>
        </div>
        {!isEditing && (
          <Button
            onClick={() => setIsEditing(true)}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            data-testid="edit-profile-btn"
          >
            Edit Profile
          </Button>
        )}
      </div>

      {/* Profile Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white flex items-center gap-3">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <span className="text-slate-900 font-bold text-2xl">
                {profile.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{profile.name}</h2>
              <p className="text-slate-400 text-sm font-normal">Partner Account</p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="profile-form">
              <div>
                <Label htmlFor="name" className="text-slate-300 mb-2 block">
                  Full Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleFormChange}
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-300 mb-2 block">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleFormChange}
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="profile-email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="mobile" className="text-slate-300 mb-2 block">
                  Mobile Number
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    required
                    value={formData.mobile}
                    onChange={handleFormChange}
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="profile-mobile-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({
                      name: profile.name,
                      email: profile.email,
                      mobile: profile.mobile,
                    });
                  }}
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl">
                  <div className="p-3 rounded-lg bg-slate-800">
                    <Mail className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Email</p>
                    <p className="text-white font-medium">{profile.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl">
                  <div className="p-3 rounded-lg bg-slate-800">
                    <Phone className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Mobile</p>
                    <p className="text-white font-medium">{profile.mobile}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl">
                  <div className="p-3 rounded-lg bg-slate-800">
                    <Calendar className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Member Since</p>
                    <p className="text-white font-medium">
                      {new Date(profile.created_at).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl">
                  <div className="p-3 rounded-lg bg-slate-800">
                    <User className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Status</p>
                    <p className={`font-medium ${profile.status === 'Active' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {profile.status}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerProfile;
